/**
 * GroupModal.tsx — the spatial node-group editor.
 *
 * Mirrors NodeModal's shape (name + ID pattern, live tick-readout KV grid,
 * computed-metrics editor) but a group has no engine semantics: no kind, no
 * gate, no params, no agent. The tick readout shows the group's AGGREGATED
 * Inflow/Outflow/Stock (summed across member nodes — see balance.ts) plus one
 * cell per group `computedMetric`, evaluated against a scope whose `self` is
 * the summed member state.
 *
 * Read-only when `editable` is false (view mode).
 */
import { useState } from 'react';
import { Plus, Check } from '@phosphor-icons/react';

import type { ComputedMetric, NodeGroup } from '../engine/types';
import type { GroupBalance } from '../engine/balance';
import { Modal } from './Modal';
import { MetricsEditModal } from './MetricsEditModal';

interface GroupModalProps {
  group: NodeGroup;
  /** Aggregated balance for the group (from useSim.groupBalance). */
  balance?: GroupBalance;
  /** Computed metric values keyed by metric key (from useSim.groupMetrics). */
  metricValues?: Record<string, number>;
  /** Scope for live expression linting in the metrics sub-modal. Built by the
   *  App from the group's summed-member state. */
  scope?: Record<string, unknown>;
  editable: boolean;
  /** Member node labels for a read-only "members" listing. */
  memberLabels?: string[];
  /** All existing group ids — for ID-uniqueness validation on rename. */
  existingIds?: string[];
  onClose: () => void;
  onUpdate?: (group: NodeGroup) => void;
  /** Rename the group id. Membership is spatial so no references rewrite —
   *  returns true if the new id is unique. */
  onRenameGroup?: (oldId: string, newId: string) => boolean;
}

export function GroupModal({
  group,
  balance,
  metricValues = {},
  scope,
  editable,
  memberLabels = [],
  existingIds = [],
  onClose,
  onUpdate,
  onRenameGroup,
}: GroupModalProps): JSX.Element {
  const [draft, setDraft] = useState<NodeGroup>(() => structuredClone(group));
  const [editingId, setEditingId] = useState(false);
  const [idDraft, setIdDraft] = useState(group.id);
  const [metricModalOpen, setMetricModalOpen] = useState(false);

  const commit = (next: NodeGroup) => {
    setDraft(next);
    onUpdate?.(next);
  };

  const idTaken = editingId && idDraft !== group.id && existingIds.includes(idDraft);
  const commitId = () => {
    if (idTaken || idDraft.length === 0 || idDraft === group.id) {
      setEditingId(false);
      return;
    }
    if (onRenameGroup?.(group.id, idDraft)) {
      setDraft((d) => ({ ...d, id: idDraft }));
    }
    setEditingId(false);
  };

  // ─── Group computed-metrics editor (mirrors NodeModal) ───────────────────
  const addComputedMetric = () => {
    const m: ComputedMetric = {
      key: `metric_${Math.floor(Math.random() * 1e6)}`,
      label: 'New Metric',
      expr: 'self.stock',
    };
    commit({ ...draft, computedMetrics: [...(draft.computedMetrics ?? []), m] });
  };
  const updateComputedMetric = (idx: number, patch: Partial<ComputedMetric>) => {
    if (!draft.computedMetrics) return;
    commit({
      ...draft,
      computedMetrics: draft.computedMetrics.map((m, i) => (i === idx ? { ...m, ...patch } : m)),
    });
  };
  const removeComputedMetric = (idx: number) => {
    if (!draft.computedMetrics) return;
    commit({ ...draft, computedMetrics: draft.computedMetrics.filter((_, i) => i !== idx) });
  };
  const renameComputedMetric = (idx: number, newKey: string): boolean => {
    const cur = draft.computedMetrics?.[idx];
    if (!cur || cur.key === newKey) return false;
    if ((draft.computedMetrics ?? []).some((m, i) => i !== idx && m.key === newKey)) return false;
    updateComputedMetric(idx, { key: newKey });
    return true;
  };

  // The header slot carries ONLY the colour picker — no text label, no "group"
  // readout. In view mode the picker becomes a static swatch.
  const headerLeft = editable ? (
    <label className="group-color-header" title="Group colour">
      <input
        type="color"
        className="group-color-input"
        value={draft.color ?? '#4c72b0'}
        onChange={(e) => commit({ ...draft, color: e.target.value })}
      />
    </label>
  ) : (
    <span
      className="group-color-header-swatch"
      title="Group colour"
      style={{ backgroundColor: draft.color ?? '#4c72b0' }}
    />
  );

  return (
    <Modal headerLeft={headerLeft} onClose={onClose}>
      {/* ─── Name + ID (same pattern as NodeModal) ─────────────────────── */}
      <section className="modal-section">
        {editable ? (
          <div className="node-name-group">
            <input
              type="text"
              className="text-input node-name-input"
              placeholder="Group name"
              value={draft.label}
              onChange={(e) => commit({ ...draft, label: e.target.value })}
            />
            <button
              type="button"
              className="node-id-addon"
              title="ID"
              onClick={() => {
                if (!editable) return;
                setEditingId(true);
                setIdDraft(group.id);
              }}
              disabled={!editable}
            >
              ID
            </button>
          </div>
        ) : (
          <div className="text-input node-name-input read-only">{draft.label || '—'}</div>
        )}

        {editable && editingId && (
          <div className="node-id-edit">
            <span className="node-id-edit-label">ID</span>
            <input
              type="text"
              className={`text-input node-id-input${idTaken ? ' invalid' : ''}`}
              value={idDraft}
              onChange={(e) => setIdDraft(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitId();
                if (e.key === 'Escape') {
                  setIdDraft(group.id);
                  setEditingId(false);
                }
              }}
            />
            <button
              type="button"
              className="node-id-save"
              title="Save ID"
              disabled={idTaken || idDraft.length === 0}
              onClick={commitId}
            >
              <Check size={14} weight="bold" />
            </button>
            {idTaken && <span className="id-hint">already in use</span>}
          </div>
        )}
      </section>

      {/* ─── Aggregated tick readout ──────────────────────────────────── */}
      <section className="modal-section">
        <div className="modal-subhead">Aggregates{memberLabels.length > 0 ? ` · ${memberLabels.length} members` : ''}</div>
        <div className="modal-kv-grid node-tick-grid">
          <KV label="Inflow" value={fmt(balance?.inflow ?? 0)} />
          <KV label="Outflow" value={fmt(balance?.outflow ?? 0)} />
          <KV label="Stock" value={fmt(balance?.stock ?? 0)} />
          {(draft.computedMetrics ?? []).map((m) => {
            const v = metricValues[m.key];
            return <KV key={m.key} label={m.label || m.key} value={v == null ? '—' : fmt(v)} />;
          })}
          {editable && (
            <button
              type="button"
              className="kv-add"
              onClick={() => setMetricModalOpen(true)}
              title="Add metric"
            >
              <Plus size={14} weight="bold" />
            </button>
          )}
        </div>
      </section>

      {/* ─── Members (read-only listing of contained nodes) ───────────── */}
      {memberLabels.length > 0 && (
        <section className="modal-section">
          <div className="modal-subhead">Members</div>
          <div className="group-members">
            {memberLabels.map((label, i) => (
              <span key={i} className="group-member-chip">{label}</span>
            ))}
          </div>
        </section>
      )}

      {/* Colour lives in the header now (just the picker, no label). The
          "Background" fill toggle is in the canvas context menu, not here. */}

      {metricModalOpen && (
        <MetricsEditModal
          title="Group Metrics"
          metrics={draft.computedMetrics ?? []}
          scope={scope ?? { self: { stock: balance?.stock ?? 0, inflow: balance?.inflow ?? 0, outflow: balance?.outflow ?? 0 } }}
          onClose={() => setMetricModalOpen(false)}
          onAdd={addComputedMetric}
          onUpdate={updateComputedMetric}
          onRemove={removeComputedMetric}
          onRenameKey={renameComputedMetric}
          keyTaken={(key, idx) => (draft.computedMetrics ?? []).some((m, i) => i !== idx && m.key === key)}
        />
      )}
    </Modal>
  );
}

function KV({ label, value }: { label: string; value: string }): JSX.Element {
  return <div className="modal-kv"><span>{label}</span><code>{value}</code></div>;
}
function fmt(n: number): string {
  if (Math.abs(n) >= 10000) return (n / 1000).toFixed(1) + 'k';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
}
